'use client'

import { useEffect, useRef } from "react"

import { FormInstance } from "antd"
import { createNewBoardThunk } from 'features/boards/model/slice/boardsSlice'
import {
  selectIsAddNewBoardModalOpen,
  selectModalBoardData
} from "features/common/model/selectors/appSelectors"
import {
  actions,
  setIsAddNewBoardModalOpen
} from "features/common/model/slice/appSlice"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { Button, Form, Input, Modal, Typography } from "shared/ui"
import { rootBoardIdSelector } from "features/user/model/selectors/userSelectors"
import styled from "styled-components"

interface FieldType {
  name: string
}

export interface ModalBoardData {
  parentId: string
  createThunk?: (name: string) => void
}

export const AddNewBoardModal = () => {
  const dispatch = useAppDispatch()
  const isAddNewBoardModalOpen = useAppSelector(selectIsAddNewBoardModalOpen)
  const formRef = useRef<FormInstance>(null)
  const inputRef = useRef<Input>(null)
  const modalBoardData = useAppSelector(selectModalBoardData) as ModalBoardData | null
  const rootBoardId = useAppSelector(rootBoardIdSelector)

  useEffect(() => {
    if (isAddNewBoardModalOpen) {
      formRef.current?.resetFields()

      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    } else {
      dispatch(actions.setModalBoardData(null))
    }
  }, [isAddNewBoardModalOpen])

  const handleClose = () => {
    dispatch(setIsAddNewBoardModalOpen(false))
  }

  const handleFinish = (values: FieldType) => {
    if (modalBoardData?.createThunk) {
      modalBoardData.createThunk(values.name)
    } else {
      const parentId = modalBoardData?.parentId ?? rootBoardId
      dispatch(createNewBoardThunk({ name: values.name, parentId }))
    }
    handleClose()
  }

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo)
  }

  return (
    <Modal
      title="Create a new board"
      className="add-new-board-modal"
      open={isAddNewBoardModalOpen}
      width={440}
      destroyOnClose
      onCancel={handleClose}
      footer={null}
      style={{ marginTop: 50 }}
    >
      <Typography.Text type="secondary">
        Manage groups of files on the boards in one place
      </Typography.Text>

      <SContentContainer>
        <Form
          ref={formRef}
          onFinish={handleFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          className="add-board-form"
          requiredMark="optional"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter board name' }]}
          >
            <SInput
              ref={inputRef}
              placeholder="Enter name"
            />
          </Form.Item>

          <SButtonsContainer>
            <Button size="large"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button size="large"
              type="primary"
              htmlType="submit"
            >
              Create
            </Button>
          </SButtonsContainer>
        </Form>
      </SContentContainer>
    </Modal>
  )
}

const SInput = styled(Input)`
  height: 40px;
`

const SContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;

  .add-board-form {
    margin-top: 5px;
  }
`

const SButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`
